/**
 * A span exporter wrapper that holds spans until the session is authenticated.
 *
 * Why: the telemetry proxy (`POST /api/telemetry/v1/traces`) is auth-gated, so
 * spans produced before login (app bootstrap, the login flow itself) would be
 * rejected with 401 and dropped — the BatchSpanProcessor has no retry. This
 * wrapper buffers those spans in memory and, once `markAuthenticated()` is
 * called after a successful login, drains them through the real exporter. After
 * that point it's a transparent pass-through.
 */

import { ExportResultCode, type ExportResult } from '@opentelemetry/core';
import type { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-web';

export class BufferingSpanExporter implements SpanExporter {
  /** Cap so a session that never logs in can't grow the buffer without bound. */
  private static readonly MAX_PENDING = 500;

  private authenticated = false;
  private readonly pending: ReadableSpan[] = [];

  constructor(private readonly inner: SpanExporter) {}

  export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
    if (this.authenticated) {
      this.inner.export(spans, resultCallback);
      return;
    }

    // Hold the spans until login. Report SUCCESS so the BatchSpanProcessor
    // treats them as handed off (it would otherwise drop them, with no retry).
    for (const span of spans) {
      this.pending.push(span);
    }
    if (this.pending.length > BufferingSpanExporter.MAX_PENDING) {
      // Keep the most recent spans; oldest pre-login spans are the least useful.
      this.pending.splice(0, this.pending.length - BufferingSpanExporter.MAX_PENDING);
    }
    resultCallback({ code: ExportResultCode.SUCCESS });
  }

  /**
   * Switch to pass-through mode and flush everything buffered so far. Idempotent
   * — calling it again with an empty buffer does nothing.
   */
  markAuthenticated(): void {
    this.authenticated = true;
    if (this.pending.length === 0) {
      return;
    }
    const drained = this.pending.splice(0, this.pending.length);
    // Fire-and-forget: a failed post-login flush is dropped like any other
    // export failure. We deliberately don't re-buffer, to avoid a retry loop.
    this.inner.export(drained, () => {});
  }

  shutdown(): Promise<void> {
    this.pending.length = 0;
    return this.inner.shutdown();
  }

  forceFlush(): Promise<void> {
    return this.inner.forceFlush?.() ?? Promise.resolve();
  }
}
